import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'react-toastify';
import type { TelegramAccessMode, TelegramChannel } from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { telegramAdminApi } from '../api/telegramApi';
import { TelegramUserWhitelistSection } from './TelegramUserWhitelistSection';

type TelegramChannelPanelProps = {
  instanceBotId: string;
  botName: string;
  onChannelChange?: () => void;
};

type ChannelFormState = {
  botToken: string;
  accessMode: TelegramAccessMode;
  whitelistRejectionEnabled: boolean;
  whitelistRejectionMessage: string;
};

const emptyForm: ChannelFormState = {
  botToken: '',
  accessMode: 'OPEN',
  whitelistRejectionEnabled: false,
  whitelistRejectionMessage: '',
};

export function TelegramChannelPanel({
  instanceBotId,
  botName,
  onChannelChange,
}: TelegramChannelPanelProps) {
  const [channel, setChannel] = useState<TelegramChannel | null>(null);
  const [form, setForm] = useState<ChannelFormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadChannel = useCallback(async () => {
    try {
      setLoading(true);
      const existing = await telegramAdminApi.getChannelByInstanceBot(instanceBotId);
      setChannel(existing);
      if (existing) {
        setForm({
          botToken: '',
          accessMode: existing.accessMode,
          whitelistRejectionEnabled: existing.whitelistRejectionEnabled,
          whitelistRejectionMessage: existing.whitelistRejectionMessage ?? '',
        });
      } else {
        setForm(emptyForm);
      }
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar Telegram');
    } finally {
      setLoading(false);
    }
  }, [instanceBotId]);

  useEffect(() => {
    loadChannel();
  }, [loadChannel]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const trimmedRejection = form.whitelistRejectionMessage.trim();
      if (channel) {
        const updated = await telegramAdminApi.updateChannel(channel.id, {
          botToken: form.botToken.trim() || undefined,
          accessMode: form.accessMode,
          whitelistRejectionEnabled: form.whitelistRejectionEnabled,
          whitelistRejectionMessage: trimmedRejection || null,
        });
        setChannel(updated);
        setForm(prev => ({ ...prev, botToken: '' }));
        toast.success('Canal Telegram actualizado');
        onChannelChange?.();
      } else {
        if (!form.botToken.trim() || form.botToken.trim().length < 20) {
          toast.error('El token del bot es requerido (desde BotFather)');
          return;
        }
        const created = await telegramAdminApi.createChannel({
          instanceBotId,
          botToken: form.botToken.trim(),
          accessMode: form.accessMode,
          whitelistRejectionEnabled: form.whitelistRejectionEnabled,
          whitelistRejectionMessage: trimmedRejection || undefined,
        });
        setChannel(created);
        setForm(prev => ({ ...prev, botToken: '' }));
        toast.success('Canal Telegram creado');
        onChannelChange?.();
      }
    } catch (error) {
      toast.error((error as Error).message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleHealthCheck = async () => {
    if (!channel) {
      return;
    }
    try {
      const result = await telegramAdminApi.healthCheck(channel.id);
      toast.success(`Health: ${result.status}${result.botUsername ? ` (@${result.botUsername})` : ''}`);
      await loadChannel();
    } catch (error) {
      toast.error((error as Error).message || 'Health check falló');
    }
  };

  const handleRotateSecret = async () => {
    if (!channel) {
      return;
    }
    try {
      await telegramAdminApi.rotateSecret(channel.id);
      toast.success('Secreto de webhook rotado y re-registrado');
      await loadChannel();
    } catch (error) {
      toast.error((error as Error).message || 'Error al rotar secreto');
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground py-4'>Cargando canal Telegram...</p>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Canal Telegram del bot &quot;{botName}&quot;
          {channel?.botUsername && (
            <Badge variant='secondary' className='ml-2'>@{channel.botUsername}</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Webhook:{' '}
          <code className='text-xs'>
            {channel?.webhookUrl ?? 'configurar TELEGRAM_WEBHOOK_BASE_URL (incl. /api/webhooks/telegram)'}
          </code>
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='grid gap-4 md:grid-cols-2'>
          <div>
            <Label>Token del bot {channel ? '(dejar vacío para no cambiar)' : ''}</Label>
            <Input
              type='password'
              value={form.botToken}
              onChange={e => setForm({ ...form, botToken: e.target.value })}
              placeholder='123456:ABC-DEF...'
            />
          </div>
          <div>
            <Label>Modo de acceso</Label>
            <select
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              value={form.accessMode}
              onChange={e =>
                setForm({ ...form, accessMode: e.target.value as TelegramAccessMode })
              }
            >
              <option value='OPEN'>Abierto</option>
              <option value='WHITELIST_USERS'>Whitelist de usuarios</option>
            </select>
          </div>
        </div>

        <div className='flex flex-wrap gap-2'>
          <Button type='button' onClick={() => void handleSave()} disabled={saving}>
            {channel ? 'Guardar cambios' : 'Crear canal'}
          </Button>
          {channel && (
            <>
              <Button type='button' variant='outline' onClick={() => void handleHealthCheck()}>
                <RefreshCw className='h-4 w-4 mr-1' />
                Health check
              </Button>
              <Button type='button' variant='outline' onClick={() => void handleRotateSecret()}>
                Rotar secreto webhook
              </Button>
            </>
          )}
        </div>

        {channel && (
          <Tabs defaultValue='whitelist'>
            <TabsList>
              <TabsTrigger value='whitelist'>Whitelist</TabsTrigger>
            </TabsList>
            <TabsContent value='whitelist' className='mt-4'>
              <TelegramUserWhitelistSection
                channelId={channel.id}
                accessMode={form.accessMode}
                whitelistRejectionEnabled={form.whitelistRejectionEnabled}
                whitelistRejectionMessage={form.whitelistRejectionMessage}
                onRejectionChange={(enabled, message) =>
                  setForm({
                    ...form,
                    whitelistRejectionEnabled: enabled,
                    whitelistRejectionMessage: message,
                  })
                }
                showRejectionSettings
                showSaveReminder
                compact
              />
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}
